import './PythonApplicationEditor.css';

import React, { FC, useState } from 'react';

import { MonacoEditor } from '../../components/MonacoEditor';
import { LogsOutput } from './LogsOutput';

import TokenizeThis from "tokenize-this";
// This import ensures that monaco-editor-webpack plugin will work (see here: https://www.npmjs.com/package/monaco-editor-webpack-plugin)
import * as monaco from 'monaco-editor/esm/vs/editor/editor.api';

import { observer } from 'mobx-react-lite';
import { EditorBackButton, ApplicationSelectDropdown } from "../visual_sequence_editor/left_pane";
import cx from 'classnames';

import { ApplicationState, ApplicationTypes } from '../../machinelogic/constants';
import { Library, ApplicationBase, PythonApplication, Environment, User } from '../../machinelogic/types';

import { Spinner } from '../../../src/components/Spinner';
import icons from '../../icons';

import { getMachineLogicConfiguration } from '../../machinelogic';

declare global {
  interface Window {
    _main: {
      ifTouchScreen: () => boolean;
    }
  }
}

import Split from 'react-split';
import { PlayerControls } from '../visual_sequence_editor/player_controls/PlayerControls';

monaco.editor.defineTheme('python-theme', {
  base: 'vs',
  inherit: true,
  rules: [],

  // See list of color names here: https://github.com/microsoft/monaco-editor/issues/1631
  colors: {
    'editorGutter.background': '#f4f4f4',
    'editorLineNumber.foreground': '#A1A1A1'
  },
});

interface PythonApplicationEditorProps {
  library: Library;
  goBackToLandingPage: () => void;
  onApplicationUploaded: (application: ApplicationBase | null) => void;
  user: User | undefined;
  environment: Environment;
  showAutoSaveIndicator: boolean;
  showDocumentationSpan: boolean;
}

export const PythonApplicationEditor: FC<PythonApplicationEditorProps> = observer(({library, goBackToLandingPage, onApplicationUploaded, user, environment, showAutoSaveIndicator, showDocumentationSpan}) => {
  const [loading, setLoading] = useState<boolean>(false);

  /*
   * Called when the value of the code editor changes.
   * @param filename File name
   * @param newCode New code
   */
  const onSourceCodeChanged = (filename: string, code: string | undefined) => {
    // TEMP-NOTE: this is not pretty, we need to get current app otherwise we would overwrite the code of the last selected one.
    const currentApplication = library.currentApplication as PythonApplication;
    currentApplication.changeSourceFile(filename, code);
  };

  if (library.currentApplication?.getType() !== ApplicationTypes.Python) {
    return null;
  }

  const application = library.currentApplication as PythonApplication;
  const selectedSourceFile = application.getSingleSourceFile();

  const isOnPendant = environment === Environment.PENDANT;
  const isOnCad = environment === Environment.CAD;
  const isTouchScreen = window._main?.ifTouchScreen?.();

  const documentationUrl = getMachineLogicConfiguration().pythonDocumentationUrl;

  return (
    <div className={cx('python_application_editor_container', {
      'environment-pendant': isOnPendant,
    })}>
      <div className='python_application_treeview'>
        <div className={cx('left-pane-container', {
          'environment-cad': isOnCad,
          'environment-pendant': isOnPendant,
          'environment-touchscreen': isTouchScreen,
        })}>
          <EditorBackButton goBack={goBackToLandingPage} disabled={library.currentApplication?.isRunning()}/>
          <div className="ml_library v_layout bordered spaced" >
            <div className="library-widget-container v_layout">
              <ApplicationSelectDropdown
                onAppSelected={() => {
                  setLoading(true);
                  setTimeout(() => {
                    setLoading(false);
                  }, 150);
                }}
                library={library}
                isRunning={library.currentApplication?.isRunning()}
                applicationType={ApplicationTypes.Python}
                user={user}
                onApplicationUploaded={onApplicationUploaded}
                allowAppCreation={true}
              />
            </div>
            {
              showDocumentationSpan &&
                <span className="python_application_left_pane_need_help_span">
                  Need help with Python? <a className="python_application_left_pane_need_help_anchor" href={documentationUrl} target="_blank"> See resources </a>
                </span>
            }
          </div>
        </div>

      </div>
      { loading ?
        <Spinner spinnerText={'Loading Application'}/>
        :
        <div className='python_application_editor_view'>
          {
            showAutoSaveIndicator &&
          <span className='python_application_view_auto_save'>
            <i className={icons.checkmark} /> AutoSave
          </span>
          }
          <div className='python_application_view_split_container'>
            <Split
              className='python_application_view_split'
              direction='vertical'
              minSize={120}
              maxSize={720}
              snapOffset={0}
              sizes={[75, 25]}
            >
              <MonacoEditor
                source={selectedSourceFile?.code}
                filename={selectedSourceFile?.filename}
                onChange={onSourceCodeChanged}
                className='python_application_editor_monaco bordered'
                language="python"
                playing={application.mState === ApplicationState.RUNNING}
                theme={"python-theme"}
              />
              <LogsOutput application={application} limit={1000}/>

            </Split>
          </div>
          <PlayerControls
            library={library}
            isRobotReady={() => true}
            smallButtonSize={isOnCad}
            runTimeConfig={{}}
          />
        </div>
      }
    </div>
  );
});

/**
 * TODO: This function is unused for now, and is meant to provide an example
 * of how we can include custom completion behavior in python. The one caveat
 * is that this completion syntax defines completions globallly instead of
 * locally such that ALL monaco-editor instances will inherit it. This is not
 * ideal. Until we have a solid completion framework, I vote for leaving it out.
 */
function defineCustomCompletion() {
  const tokenizerConfigs = {
    shouldTokenize: [ ],
    shouldMatch: [],
    delimiterList: [' ', "\n", "\r", "\t", ':', ',', '.', ';', '(', ')', '[', ']', '{', '}', '=', '<', '>'],
    shouldDelimitBy: [' ', "\n", "\r", "\t", ':', ',', '.', ';', '(', ')', '[', ']', '{', '}', '=', '<', '>'],
    convertLiterals: false,
  };

  const tokenizer = new TokenizeThis(tokenizerConfigs);

  // NOTE: if we add custom completion, we loose the default one. We need to recreate it ourselves.
  monaco.languages.registerCompletionItemProvider('python', {
    provideCompletionItems: (
      model: monaco.editor.ITextModel,
      position: monaco.Position,
      context: monaco.languages.CompletionContext,
      token: monaco.CancellationToken
    ): monaco.languages.ProviderResult<monaco.languages.CompletionList> => {

      const word = model.getWordUntilPosition(position);
      const range = {
        startLineNumber: position.lineNumber,
        endLineNumber: position.lineNumber,
        startColumn: word.startColumn,
        endColumn: word.endColumn,
      };

      const suggestions: monaco.languages.CompletionItem[] = [
      /* commented, here to give an idea of what we can do later
      {
        label: 'help.insert_snippet',
        kind: monaco.languages.CompletionItemKind.Keyword,
        insertText: `from mmprogrammatic import Library\n\n` +
          `library = Library()\n` +
          `axis = library.getAxis("Linear")\n`,
        range: range
      }
      */
      ];
      // Until we have a language server with the programmatic API, we can add completion words here
      const apiWords: string[] = ['machinelogic', 'Library', 'moveRelative', 'getAxis'];

      apiWords.forEach(apiWord => suggestions.push({
        label: apiWord,
        kind: monaco.languages.CompletionItemKind.Keyword,
        insertText: apiWord,
        range: range
      }));

      tokenizer.tokenize(model.getValue(),
        (t)=> {
          if(apiWords.includes(t)) { return; }
          apiWords.push(t);
          suggestions.push({
            label: t,
            kind: monaco.languages.CompletionItemKind.Keyword,
            insertText: t,
            range: range
          });
        }
      );

      return { suggestions: suggestions };
    }
  });
}

monaco.editor.addCommand({
  id: 'design-save',
  run: () => {
    // TODO: Execute a save. This is a placeholder for now to prove out the concept
    console.log('Saved!');
  }
});
monaco.editor.addKeybindingRule({
  keybinding: monaco.KeyMod.CtrlCmd | monaco.KeyCode.KeyS,
  command: 'design-save'
});